import { NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import type { DateTime, Utc } from "@tento-chrono";
import { type Actor, type EventId, type Option } from "@tento-lona/sheets";
import type { EventItem, EventItemAllDay, EventItemTimed, EventPatch } from "@tento-lona/sheets";
import type {
  AllDayRange,
  EventsClient,
  RecurringEditScope,
  SheetRow,
  TimedRange,
} from "@tento-lona";

/**
 * One occurrence move (`EventsSource.moveOccurrence`): the occurrence and its
 * new range, of one kind — a timed occurrence to a `TimedRange`, an all-day
 * one to an `AllDayRange`.
 */
export type OccurrenceMove =
  | { readonly kind: "timed"; readonly event: EventItemTimed; readonly range: TimedRange }
  | { readonly kind: "all-day"; readonly event: EventItemAllDay; readonly range: AllDayRange };

import { EVENTS_FACADE } from "@tento-lona/sheets/platform";
import type { EventsFacade } from "@tento-lona/sheets/platform";
import { liveVariantOf } from "../../../sheet/live-variant.ts";
import type { CalendarListConfig } from "@tento-lona/platform";
import { EventAccess } from "../../../sheet/gestures/event-access.ts";

/**
 * Events-access seam for `CalendarMonthActions`. The actions read and
 * mutate events only through this interface, decoupling them from the
 * aggregator. The sole implementation, `AggregatingEventsSource`, merges
 * reads across every constituent cal-* source so events from all
 * connected calendars render in the month grid.
 *
 * Part of converging the `*.event` renderers onto one strategy — see
 * the calendar-month-multicalendar-events project doc.
 *
 * `peek`, `peekInRange`, and `peekAllDayInRange` mirror the
 * `EventsFacade` read signatures exactly (returning the sheets-side
 * `EventItem` types the callers already wrap), so the aggregating impl
 * can merge facade results without a shape change at the call sites.
 */
export interface EventsSource {
  /** Prefetch events overlapping a UTC range across all backing sources. */
  ensureLoaded(range: DateTime.Range<Utc>): Promise<void>;

  /** Find one event by id (first match across sources). */
  peek(id: EventId): ReturnType<EventsFacade["peek"]>;

  /** Partial-day events overlapping a UTC range, merged across sources. */
  peekInRange(
    range: DateTime.Range<Utc>,
  ): ReturnType<EventsFacade["peekInRange"]>;

  /** Full-day events overlapping a date range, merged across sources. */
  peekAllDayInRange(
    range: NaiveDate.Range,
  ): ReturnType<EventsFacade["peekAllDayInRange"]>;

  /**
   * Persist a change to an existing event. Routed by the event's
   * carried `origin` (its data-owning source row) through the SDK's
   * `sdk.events` — never by re-walking to the projection the event was
   * read through, and with no row/key/prev-state to thread — written by
   * `actor`.
   */
  update(event: EventItem, patch: EventPatch, actor: Actor): Promise<EventItem>;

  /**
   * Persist a drag settle through the SDK's durable handoff
   * (`EventsClient.updateDurable`): the write survives an immediate reload,
   * which aborts the plain POST of `update`. `settled` rejects with
   * `EventWriteError` once the change was taken back.
   */
  updateDurable(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): EventsClient.DurableUpdate;

  /**
   * Move/resize one occurrence of a recurring event. The gesture/UI
   * layer asks the user which scope to apply (this / following / all)
   * via the recurring-edit picker, then dispatches through here.
   */
  moveOccurrence(
    move: OccurrenceMove,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void>;

  /**
   * The source row that owns an existing event (for its details panel and
   * context menu), as the SDK month read names it: null when no source owns
   * it, never another calendar's row.
   */
  ownerOf(eventId: EventId): Option<SheetRow.Joined.Handle>;

  /**
   * The row a new event is created in: the SDK's create target, only when
   * the list offers creating and the target takes writes (`EventAccess.Create`);
   * null otherwise (N3-1).
   */
  createTarget(): Option<SheetRow.Joined.Handle>;

  /**
   * Whether the list accepts edits (move, resize, create): false for a
   * read-only list config. Gestures check it before they start a write.
   */
  canWrite(): boolean;
}

/**
 * What the month grid reads of the SDK month read (sdk-to-dom-8): only the
 * members, the owner's joined row and the create target's joined row.
 * `CalendarEventRead` satisfies it structurally; the policy (union order,
 * first id wins, owner, create row) is the SDK's.
 */
export interface CalendarMonthRead {
  /** `CalendarEventRead.peekMonthTimed`: the month union of `range`. */
  peekMonthTimed(range: DateTime.Range<Utc>): readonly { readonly item: EventItemTimed }[];
  /** `CalendarEventRead.peekMonthAllDay`: the month union of `days`. */
  peekMonthAllDay(days: NaiveDate.Range): readonly { readonly item: EventItemAllDay }[];
  /** `CalendarEventRead.peekMonthOwner`: the first source holding `id`. */
  peekMonthOwner(id: EventId): Option<{ readonly joined: Option<SheetRow.Joined.Handle> }>;
  /** `CalendarEventRead.peekCreateTarget`: the source a new event is created in, and whether it is writable. */
  peekCreateTarget(): Option<{
    readonly source: { readonly joined: Option<SheetRow.Joined.Handle> };
    readonly writable: boolean;
  }>;
  /** `CalendarEventRead.peekMonthSources`: the month's source rows, in union order. */
  peekMonthSources(): readonly { readonly joined: Option<SheetRow.Joined.Handle> }[];
  /** `CalendarEventRead.peekListConfig`: the list's access state. */
  peekListConfig(): { readonly access: CalendarListConfig["access"] };
}

/**
 * Host the `AggregatingEventsSource` reads through:
 * `CalendarListAliasPlugin` supplies the calendar row's SDK month read and
 * its membership, so the aggregating source never imports the plugin and
 * the month policy stays the SDK's.
 */
export interface CalendarMonthEventsHost {
  /**
   * The SDK month read of the calendar row (sdk-to-dom-8): the one source
   * enumeration (N3-2) and the union, owner, create-target and access
   * policy the month grid reads through.
   */
  monthRead: CalendarMonthRead;

  /**
   * The list's membership (`CalendarEventRead.membership` with the user's
   * calendar visibility): which items the month shows, as the day view
   * does (N3-1). A function the source calls on every read, never a
   * predicate captured once: the predicate snapshots the list's config, and
   * the source outlives config edits (the month actions are cached per row),
   * so a captured one kept the first bind's filter (N4-2).
   */
  member: () => (item: EventItemTimed | EventItemAllDay) => boolean;

  /** The narrow write capability the month renderer needs. Writes
   *  resolve the source row from `event.origin` internally. Satisfied
   *  structurally by the SDK's `sdk.events` — but the renderer depends
   *  only on this method, never the whole client (per the
   *  no-threaded-`EventsClient` rule in `interactable-item.ts`). */
  events: EventWriter;
}

/**
 * Minimal event-write seam consumed by `AggregatingEventsSource`. Kept
 * narrow (just the methods the month renderer calls) so sheets-ui
 * doesn't take a dependency on the full SDK `EventsClient`; `sdk.events`
 * satisfies it structurally. Grow this as delete / move migrate onto the
 * source.
 */
export interface EventWriter {
  /** Persist a field change to an existing event, routed by its carried
   *  `origin`, written by `actor`. */
  update(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): Promise<EventItem>;

  /** The drag-settle write through the durable handoff; see
   *  `EventsSource.updateDurable`. */
  updateDurable(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): EventsClient.DurableUpdate;

  /** Move/resize one occurrence of a recurring event under the chosen
   *  edit scope. Composes EXDATE/UNTIL on the master with a new event
   *  create at the destination range. */
  moveOccurrence(
    event: EventItemTimed,
    newRange: TimedRange,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void>;
  moveOccurrence(
    event: EventItemAllDay,
    newRange: AllDayRange,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void>;
}

/**
 * Multi-source events access: merges reads across every constituent
 * cal-* source so events from all connected calendars render in the
 * month grid. `peek` / `peekInRange` / `ownerOf` merge across the
 * constituent facades; `update` / `updateDurable` / `moveOccurrence` route
 * writes by the event's carried `origin` through `sdk.events`.
 *
 * `CalendarListAliasPlugin` supplies the host; this is the only `EventsSource`
 * implementation (grid.event no longer constructs actions).
 */
export class AggregatingEventsSource implements EventsSource {
  readonly #host: CalendarMonthEventsHost;
  constructor(host: CalendarMonthEventsHost) {
    this.#host = host;
  }

  #facadeOf(row: SheetRow.Joined.Handle): Option<EventsFacade> {
    return row.findFacade(EVENTS_FACADE) ?? null;
  }

  /** The month's source rows (the SDK's enumeration, N3-2). */
  #rows(): SheetRow.Joined.Handle[] {
    return this.#host.monthRead.peekMonthSources().flatMap((source) =>
      source.joined == null ? [] : [source.joined]
    );
  }

  async ensureLoaded(range: DateTime.Range<Utc>): Promise<void> {
    const naive = new NaiveDate.Range(range.start.date, range.end.date);
    await Promise.all(
      this.#rows().map((row) => liveVariantOf(row)?.ensureLoaded(naive, null)),
    );
  }

  /** The event `id` as its owning month source holds it (the source `ownerOf` names). */
  peek(id: EventId): ReturnType<EventsFacade["peek"]> {
    const owner = this.#host.monthRead.peekMonthOwner(id)?.joined ?? null;
    return owner == null ? null : this.#facadeOf(owner)?.peek(id) ?? null;
  }

  canWrite(): boolean {
    return this.#list() === "editable";
  }

  /** The list half of the access decision, from the month read's list config. */
  #list(): EventAccess.List {
    return EventAccess.fromListConfig(this.#host.monthRead.peekListConfig().access);
  }

  /** The month union of `range` (source order, then start-then-id; first id wins): the SDK's policy. */
  peekInRange(
    range: DateTime.Range<Utc>,
  ): ReturnType<EventsFacade["peekInRange"]> {
    const member = this.#host.member();
    return this.#host.monthRead.peekMonthTimed(range).flatMap(({ item }) => member(item) ? [item] : []);
  }

  peekAllDayInRange(
    range: NaiveDate.Range,
  ): ReturnType<EventsFacade["peekAllDayInRange"]> {
    const member = this.#host.member();
    return this.#host.monthRead.peekMonthAllDay(range).flatMap(({ item }) => member(item) ? [item] : []);
  }

  update(event: EventItem, patch: EventPatch, actor: Actor): Promise<EventItem> {
    // Routed by the event's carried origin inside `sdk.events` — the
    // aggregating source itself is read-only.
    return this.#host.events.update(event, patch, actor);
  }

  updateDurable(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): EventsClient.DurableUpdate {
    return this.#host.events.updateDurable(event, patch, actor);
  }

  moveOccurrence(
    move: OccurrenceMove,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void> {
    return move.kind === "timed"
      ? this.#host.events.moveOccurrence(move.event, move.range, scope, actor)
      : this.#host.events.moveOccurrence(move.event, move.range, scope, actor);
  }

  ownerOf(eventId: EventId): Option<SheetRow.Joined.Handle> {
    const owner = this.#host.monthRead.peekMonthOwner(eventId)?.joined ?? null;
    // The user can see this event but its details / context menu will not
    // open: leave a breadcrumb instead of failing silently.
    if (owner == null) {
      Tracing.log("calendar/month-event-unowned", {
        eventId: eventId.toString(),
        sources: this.#rows().length,
      });
    }
    return owner;
  }

  createTarget(): Option<SheetRow.Joined.Handle> {
    // A new event goes to the SDK's create target, only when the list offers
    // creating and the target takes writes (`EventAccess.Create`, N3-1).
    if (!EventAccess.Create.offered(this.#list())) return null;
    const target = this.#host.monthRead.peekCreateTarget();
    return target != null && EventAccess.Create.accepts(target) ? target.source.joined : null;
  }
}
